import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useUserContext } from '../../../context_providers/user_context/UserProvider';
import Loading from '../../../components/loading/Loading';
import {
  exportManufacturingResults,
  getManufacturingMetrics,
} from '../../../repository/CompassProTestDAO';
import dayjs from 'dayjs';
import DateRangeControls from './sections/DateRangeControls';
import ManufacturingResultsSummary from './sections/ManufacturingResultsSummary';

const Manufacturing = () => {
  const { bearerToken } = useUserContext();
  const today = useMemo(() => dayjs(), []);
  const maxSelectableDate = useMemo(() => today.add(1, 'day'), [today]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [details, setDetails] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [exportError, setExportError] = useState('');

  const hasDataForExport = useMemo(() => {
    if (!metricsData) {
      return false;
    }
    if (details.length > 0) {
      return true;
    }
    return (metricsData.total_units_tested ?? 0) > 0;
  }, [metricsData, details]);

  const rangeIsValid = useMemo(() => {
    if (!startDate || !endDate) {
      return false;
    }
    if (!endDate.isAfter(startDate, 'day')) {
      return false;
    }
    return true;
  }, [startDate, endDate]);

  const formattedRange = useMemo(() => {
    if (!rangeIsValid) {
      return null;
    }
    return {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
    };
  }, [rangeIsValid, startDate, endDate]);

  const formatCount = (value) => Number(value ?? 0).toLocaleString();

  const handleFetchMetrics = async () => {
    if (!bearerToken || !formattedRange) {
      return;
    }
    setError('');
    setLoadingMetrics(true);
    try {
      const response = await getManufacturingMetrics(
        formattedRange.start,
        formattedRange.end,
        bearerToken,
      );
      if (!response.ok) {
        throw new Error('Unable to load manufacturing metrics.');
      }
      const result = await response.json();
      setMetricsData(result.metrics);
      setDetails(result.details ?? []);
    } catch (err) {
      setMetricsData(null);
      setDetails([]);
      setError(err.message || 'Unable to load manufacturing metrics.');
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleExport = async () => {
    if (!bearerToken || !formattedRange) {
      return;
    }
    setExportError('');
    setExporting(true);
    try {
      const response = await exportManufacturingResults(
        formattedRange.start,
        formattedRange.end,
        bearerToken,
      );
      if (!response.ok) {
        throw new Error('Export failed. Please try again.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const disposition = response.headers.get('Content-Disposition');
      let filename = `manufacturing-results-${formattedRange.start}-to-${formattedRange.end}.csv`;

      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/i);
        if (match?.[1]) {
          filename = match[1];
        }
      }

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message || 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleStartDateChange = (value) => {
    if (value && endDate && endDate.isBefore(value, 'day')) {
      setEndDate(null);
    }
    setStartDate(value);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
  };

  const summaryCards = useMemo(() => {
    if (!metricsData) {
      return [];
    }
    return [
      {
        label: 'Units Tested',
        value: formatCount(metricsData.total_units_tested),
      },
      {
        label: 'Units Passed',
        value: formatCount(metricsData.units_passed),
      },
      {
        label: 'Units Failed',
        value: formatCount(metricsData.units_failed),
      },
      {
        label: 'Test Yield',
        value: `${((metricsData.test_yield ?? 0) * 100).toFixed(1)}%`,
      },
    ];
  }, [metricsData]);

  const topFailures = useMemo(
    () => metricsData?.top_failure_types ?? [],
    [metricsData],
  );

  const showRangeHint = !rangeIsValid && (startDate || endDate);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={{ pb: 8 }}>
        <Stack spacing={4} sx={{ mt: 2 }}>
          <Typography variant="h4" gutterBottom>
            Manufacturing Metrics
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <DateRangeControls
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              showRangeHint={showRangeHint}
              maxSelectableDate={maxSelectableDate}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mt: 3 }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handleFetchMetrics}
                disabled={!rangeIsValid || loadingMetrics}
                sx={{ minWidth: { sm: 160 } }}
              >
                {loadingMetrics ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Load Metrics'
                )}
              </Button>

              <Button
                variant="outlined"
                startIcon={!exporting ? <DownloadIcon /> : undefined}
                onClick={handleExport}
                disabled={
                  !rangeIsValid ||
                  !hasDataForExport ||
                  exporting ||
                  loadingMetrics
                }
                sx={{ minWidth: { sm: 180 } }}
              >
                {exporting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Export Results'
                )}
              </Button>
            </Stack>
          </Paper>

          {error && <Alert severity="error">{error}</Alert>}
          {exportError && <Alert severity="error">{exportError}</Alert>}

          {loadingMetrics && !metricsData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : null}

          {metricsData && (
            <ManufacturingResultsSummary
              formattedRange={formattedRange}
              summaryCards={summaryCards}
              topFailures={topFailures}
            />
          )}

          {!loadingMetrics && metricsData && details.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No detailed test records were returned for this range.
            </Typography>
          )}
        </Stack>
      </Container>
    </LocalizationProvider>
  );
};

export default withAuthenticationRequired(Manufacturing, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
